/**
 * Formulas for cost estimates: "2*(12,5+8)*3,1", "BGF*0,35", "5%",
 * "KG(200;600)". German decimal comma or point, + - * / and parentheses, a
 * postfix % (divides by 100), named values and a few functions the caller
 * provides. Arguments are separated by ";" as in German spreadsheets, since
 * "," is the decimal separator. It is a calculator, not a scripting language:
 * parsed, never evaluated as code.
 */
const NAME = /^[A-Za-zÄÖÜäöüß_][A-Za-zÄÖÜäöüß_0-9]*/;
function tokenize(input) {
    const tokens = [];
    let i = 0;
    const s = input;
    while (i < s.length) {
        const ch = s[i];
        if (/\s/.test(ch)) {
            i++;
            continue;
        }
        if ('+-*/()×%;'.includes(ch)) {
            tokens.push({ kind: 'op', value: ch === '×' ? '*' : ch });
            i++;
            continue;
        }
        const num = /^\d+(?:[.,]\d+)?/.exec(s.slice(i));
        if (num) {
            tokens.push({ kind: 'num', value: Number(num[0].replace(',', '.')) });
            i += num[0].length;
            continue;
        }
        const name = NAME.exec(s.slice(i));
        if (name) {
            tokens.push({ kind: 'name', value: name[0] });
            i += name[0].length;
            continue;
        }
        return `Unerwartetes Zeichen „${ch}“`;
    }
    return tokens;
}
/** Recursive descent: expr = term (+|- term)*; term = factor (*|/ factor)*; factor = -factor | num | name | (expr). */
export function evaluateFormula(input, variables = {}, functions = {}) {
    if (input.trim() === '')
        return { ok: false, error: 'Leer' };
    if (input.length > 500)
        return { ok: false, error: 'Formel zu lang' };
    const tokens = tokenize(input);
    if (typeof tokens === 'string')
        return { ok: false, error: tokens };
    const lower = new Map(Object.entries(variables).map(([k, v]) => [k.toLowerCase(), v]));
    const fns = new Map(Object.entries(functions).map(([k, f]) => [k.toLowerCase(), f]));
    let pos = 0;
    let depth = 0;
    const peek = () => tokens[pos];
    const fail = (error) => { throw new Error(error); };
    const factor = () => {
        let v = primary();
        // Postfix percent: "5%" is 0,05.
        for (let t = peek(); t && t.kind === 'op' && t.value === '%'; t = peek()) {
            pos++;
            v /= 100;
        }
        return v;
    };
    const primary = () => {
        const t = tokens[pos++];
        if (!t)
            return fail('Formel endet zu früh');
        if (t.kind === 'op' && t.value === '-')
            return -primary();
        if (t.kind === 'op' && t.value === '+')
            return primary();
        if (t.kind === 'num')
            return t.value;
        if (t.kind === 'name' && tokens[pos]?.kind === 'op' && tokens[pos].value === '(') {
            const fn = fns.get(t.value.toLowerCase());
            if (!fn)
                return fail(`Unbekannte Funktion „${t.value}“`);
            pos++;
            const args = [];
            if (!(tokens[pos]?.kind === 'op' && tokens[pos].value === ')')) {
                args.push(expr());
                while (tokens[pos]?.kind === 'op' && tokens[pos].value === ';') {
                    pos++;
                    args.push(expr());
                }
            }
            const close = tokens[pos++];
            if (!close || close.kind !== 'op' || close.value !== ')')
                fail('Klammer „)“ fehlt');
            return fn(args);
        }
        if (t.kind === 'name') {
            const v = lower.get(t.value.toLowerCase());
            return v === undefined ? fail(`Unbekannte Größe „${t.value}“`) : v;
        }
        if (t.kind === 'op' && t.value === '(') {
            if (++depth > 50)
                fail('Zu tief verschachtelt');
            const v = expr();
            const close = tokens[pos++];
            if (!close || close.kind !== 'op' || close.value !== ')')
                fail('Klammer „)“ fehlt');
            depth--;
            return v;
        }
        return fail(`Unerwartet: „${t.value}“`);
    };
    const term = () => {
        let v = factor();
        for (let t = peek(); t && t.kind === 'op' && (t.value === '*' || t.value === '/'); t = peek()) {
            pos++;
            const r = factor();
            if (t.value === '/' && r === 0)
                fail('Division durch null');
            v = t.value === '*' ? v * r : v / r;
        }
        return v;
    };
    function expr() {
        let v = term();
        for (let t = peek(); t && t.kind === 'op' && (t.value === '+' || t.value === '-'); t = peek()) {
            pos++;
            const r = term();
            v = t.value === '+' ? v + r : v - r;
        }
        return v;
    }
    try {
        const value = expr();
        if (pos < tokens.length)
            return { ok: false, error: `Unerwartet: „${tokens[pos].value}“` };
        if (!Number.isFinite(value))
            return { ok: false, error: 'Ergebnis ist keine Zahl' };
        return { ok: true, value: Math.round(value * 10000) / 10000 };
    }
    catch (error) {
        return { ok: false, error: error.message };
    }
}
/** Names a formula refers to (functions excluded), for showing which values it uses. */
export function formulaNames(input) {
    const tokens = tokenize(input);
    if (typeof tokens === 'string')
        return [];
    const names = tokens.filter((t, i) => t.kind === 'name' && !(tokens[i + 1]?.kind === 'op' && tokens[i + 1].value === '('));
    return [...new Set(names.map((t) => t.value))];
}
/** Does the formula call this function, e.g. KG? */
export function formulaUses(input, fn) {
    if (!input)
        return false;
    const tokens = tokenize(input);
    if (typeof tokens === 'string')
        return false;
    return tokens.some((t, i) => t.kind === 'name' && t.value.toLowerCase() === fn.toLowerCase() && tokens[i + 1]?.kind === 'op' && tokens[i + 1].value === '(');
}
/** A valid name for a reference quantity: letters, digits, underscore, starting with a letter. */
export function isQuantityName(name) {
    return NAME.test(name) && NAME.exec(name)[0] === name && name.length <= 20;
}
/**
 * Checks only how a formula is written - names and functions may be anything,
 * since values can be defined after the formula that uses them. Returns the
 * error, or null.
 */
export function checkFormula(input, functions = ['KG']) {
    const names = Object.fromEntries(formulaNames(input).map((n) => [n, 1]));
    const fns = Object.fromEntries(functions.map((f) => [f, () => 1]));
    const r = evaluateFormula(input, names, fns);
    if (r.ok || r.error === 'Division durch null' || r.error === 'Ergebnis ist keine Zahl')
        return null;
    return r.error;
}
//# sourceMappingURL=formula.js.map